import { Fragment, useState, useEffect } from 'react';
import axios from 'axios';
import C from '../../constants/palette';
import StoneVisual from '../shared/StoneVisual';
import StatusBadge from '../shared/StatusBadge';
import AdminEditPage from './AdminEditPage';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function compareValues(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
}

function nextSort(current, key) {
  if (current.key !== key) return { key, direction: 'asc' };
  return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

function sortItems(items, sort, valueFor) {
  return [...items].sort((a, b) => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    return compareValues(valueFor(a, sort.key), valueFor(b, sort.key)) * direction;
  });
}

function pageItems(items, page, pageSize) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const start = (currentPage - 1) * pageSize;
  return {
    totalPages,
    currentPage,
    start,
    end: Math.min(start + pageSize, items.length),
    items: items.slice(start, start + pageSize),
  };
}

function inventoryValue(stone, key) {
  if (key === 'id') return stone.id;
  if (key === 'name') return stone.name;
  if (key === 'author') return stone.author || '';
  if (key === 'price') return Number(stone.price || 0);
  if (key === 'status') return stone.status || '';
  if (key === 'createdAt') return Date.parse(stone.createdAt || '') || 0;
  return '';
}

function paymentValue(payment, key) {
  const order = payment.order || {};
  if (key === 'createdAt') return Date.parse(payment.createdAt || '') || 0;
  if (key === 'treasure') return payment.treasureId || order.stoneId || '';
  if (key === 'customer') return order.buyerName || payment.email || '';
  if (key === 'amount') return Number(payment.amount || 0);
  if (key === 'provider') return payment.provider || '';
  if (key === 'status') return payment.status || '';
  return '';
}

export default function AdminDashboard({ admin, onLogout }) {
  const [nav, setNav]             = useState('inventory');
  const [inventorySearch, setInventorySearch] = useState('');
  const [paymentSearch, setPaymentSearch] = useState('');
  const [rows, setRows]           = useState([]);
  const [payments, setPayments]   = useState([]);
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [editingStone, setEditingStone] = useState(null);
  const [addingStone, setAddingStone]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [inventorySort, setInventorySort] = useState({ key: 'createdAt', direction: 'desc' });
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventoryPageSize, setInventoryPageSize] = useState(25);
  const [paymentSort, setPaymentSort] = useState({ key: 'createdAt', direction: 'desc' });
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPageSize, setPaymentPageSize] = useState(25);

  useEffect(() => {
    axios.get('/api/stones').then(res => setRows(res.data));
    axios.get('/api/payments').then(res => setPayments(res.data));
  }, []);

  useEffect(() => {
    setInventoryPage(1);
  }, [inventorySearch]);

  useEffect(() => {
    setPaymentPage(1);
    setExpandedPayment(null);
  }, [paymentSearch]);

  useEffect(() => {
    setInventoryPage(1);
  }, [inventorySort.key, inventorySort.direction, inventoryPageSize]);

  useEffect(() => {
    setPaymentPage(1);
    setExpandedPayment(null);
  }, [paymentSort.key, paymentSort.direction, paymentPageSize]);

  if (addingStone) {
    return (
      <AdminEditPage
        isNew
        onSave={s => { setRows(r => [...r, s]); setAddingStone(false); }}
        onBack={() => setAddingStone(false)}
      />
    );
  }

  if (editingStone) {
    return (
      <AdminEditPage
        stone={editingStone}
        onSave={updated => { setRows(r => r.map(s => s.id === updated.id ? updated : s)); setEditingStone(null); }}
        onBack={() => setEditingStone(null)}
      />
    );
  }

  const inventoryQuery = inventorySearch.trim().toLowerCase();
  const paymentQuery = paymentSearch.trim().toLowerCase();
  const activeSearch = nav === 'transactions' ? paymentSearch : inventorySearch;
  const activeSearchSetter = nav === 'transactions' ? setPaymentSearch : setInventorySearch;
  const searchPlaceholder = nav === 'transactions'
    ? 'Search transactions, buyers, emails, IDs...'
    : 'Search treasures, authors, IDs...';

  const filtered = rows.filter(s => {
    if (!inventoryQuery) return true;
    return s.id.toLowerCase().includes(inventoryQuery) ||
      s.name.toLowerCase().includes(inventoryQuery) ||
      (s.author || '').toLowerCase().includes(inventoryQuery);
  });
  const sortedInventory = sortItems(filtered, inventorySort, inventoryValue);
  const inventoryPageData = pageItems(sortedInventory, inventoryPage, inventoryPageSize);

  const filteredPayments = payments.filter(p => {
    const order = p.order || {};
    if (!paymentQuery) return true;
    const haystack = [
      p._id,
      p.treasureId,
      p.email,
      p.status,
      p.provider,
      p.providerPaymentId,
      order.stoneName,
      order.stoneId,
      order.buyerName,
      order.contactNumber,
      order.fulfillmentType,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(paymentQuery);
  });
  const sortedPayments = sortItems(filteredPayments, paymentSort, paymentValue);
  const paymentPageData = pageItems(sortedPayments, paymentPage, paymentPageSize);

  const deleteRow = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`/api/stones/${id}`);
      setRows(r => r.filter(s => s.id !== id));
      setDeleteTarget(null);
    } finally {
      setDeletingId(null);
    }
  };

  const toggleStatus = async (id) => {
    const stone = rows.find(s => s.id === id);
    const newStatus = stone.status === 'AVAILABLE' ? 'SOLD' : 'AVAILABLE';
    const res = await axios.patch(`/api/stones/${id}/status`, { status: newStatus });
    setRows(r => r.map(s => s.id === id ? res.data : s));
  };


  const navItems = [
    { key: 'inventory', label: 'Inventory', count: rows.filter(s => s.status === 'AVAILABLE').length },
    { key: 'transactions', label: 'Transactions', count: payments.length },
    { key: 'settings',  label: 'Settings',  count: null },
  ];

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#F4F5F7', fontFamily: 'DM Sans' }}>

      {/* ── Sidebar ── */}
      <aside style={{ width: 220, flexShrink: 0, background: '#0F172A', display: 'flex', flexDirection: 'column', padding: '0 0 24px' }}>
        <div style={{ padding: '28px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontFamily: 'Playfair Display', fontSize: 16, color: '#fff', letterSpacing: '0.04em' }}>KINGSTRADE</p>
          <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.16em', marginTop: 2 }}>CONTROL CENTER</p>
        </div>
        <nav style={{ flex: 1, padding: '20px 0' }}>
          {navItems.map(item => (
            <button key={item.key} onClick={() => setNav(item.key)} style={{
              width: '100%', padding: '12px 24px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: nav === item.key ? 'rgba(212,175,55,0.12)' : 'none',
              border: 'none',
              borderLeft: nav === item.key ? `2px solid ${C.gold}` : '2px solid transparent',
              cursor: 'pointer', textAlign: 'left',
              color: nav === item.key ? C.gold : 'rgba(255,255,255,0.4)',
              fontSize: 12, letterSpacing: '0.1em', fontWeight: 500,
              transition: 'all 0.15s',
            }}>
              {item.label.toUpperCase()}
              {item.count !== null && (
                <span style={{
                  background: nav === item.key ? C.gold : 'rgba(255,255,255,0.1)',
                  color: nav === item.key ? '#0F172A' : 'rgba(255,255,255,0.4)',
                  fontSize: 9, fontWeight: 700,
                  padding: '2px 7px', borderRadius: 100,
                  fontFamily: 'DM Mono',
                }}>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>
        {admin && (
          <div style={{ margin: '0 16px 12px', padding: '12px 10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)' }}>
            <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{admin.name}</p>
            <p style={{ color: 'rgba(255,255,255,0.32)', fontSize: 9, fontFamily: 'DM Mono', wordBreak: 'break-all' }}>{admin.email}</p>
          </div>
        )}
        <button onClick={onLogout} style={{
          margin: '0 16px', padding: '10px',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
          cursor: 'pointer', color: 'rgba(255,255,255,0.3)',
          fontSize: 10, letterSpacing: '0.16em',
        }}>
          SIGN OUT
        </button>
      </aside>

      {/* ── Main ── */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Top bar */}
        <div style={{
          height: 64, flexShrink: 0,
          background: '#fff', borderBottom: '1px solid #E8EAED',
          display: 'flex', alignItems: 'center', gap: 16, padding: '0 32px',
        }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.muted, fontSize: 13 }}>⌕</span>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={activeSearch}
              onChange={e => activeSearchSetter(e.target.value)}
              style={{
                width: '100%', maxWidth: 380,
                padding: '9px 12px 9px 36px',
                border: '1px solid #E8EAED', borderRadius: 4,
                fontSize: 13, outline: 'none', color: C.ink,
              }}
            />
          </div>
          <button style={{
            padding: '9px 20px', background: 'none', border: '1px solid #E8EAED',
            cursor: 'pointer', fontSize: 11, letterSpacing: '0.1em', color: C.muted, borderRadius: 4,
          }}>
            EXPORT DATA
          </button>
          <button onClick={() => setAddingStone(true)} style={{
            padding: '9px 20px', background: C.gold, border: 'none',
            cursor: 'pointer', fontSize: 11, letterSpacing: '0.1em', fontWeight: 600, color: '#0F172A',
            borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            + ADD TREASURE
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 32 }}>

          {/* ── Inventory ── */}
          {nav === 'inventory' && (
            <>
              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 28 }}>
                {[
                  { label: 'Total Treasures', val: rows.length },
                  { label: 'Available',       val: rows.filter(s => s.status === 'AVAILABLE').length },
                  { label: 'Sold',            val: rows.filter(s => s.status === 'SOLD').length },
                ].map(stat => (
                  <div key={stat.label} style={{ background: '#fff', padding: '20px 24px', border: '1px solid #E8EAED' }}>
                    <p style={{ fontSize: 10, color: C.muted, letterSpacing: '0.14em', fontWeight: 600, marginBottom: 8 }}>
                      {stat.label.toUpperCase()}
                    </p>
                    <p style={{ fontFamily: 'DM Mono', fontSize: 22, fontWeight: 500, color: C.ink }}>{stat.val}</p>
                  </div>
                ))}
              </div>

              {/* Table */}
              <div style={{ background: '#fff', border: '1px solid #E8EAED', overflow: 'hidden' }}>
                <TableToolbar
                  label="Inventory"
                  total={sortedInventory.length}
                  start={inventoryPageData.start}
                  end={inventoryPageData.end}
                  pageSize={inventoryPageSize}
                  onPageSizeChange={setInventoryPageSize}
                />
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E8EAED' }}>
                      <PlainHeader label="Thumbnail" />
                      <SortHeader label="ID / Name" sortKey="id" sort={inventorySort} onSort={() => setInventorySort(s => nextSort(s, 'id'))} />
                      <SortHeader label="Author" sortKey="author" sort={inventorySort} onSort={() => setInventorySort(s => nextSort(s, 'author'))} />
                      <SortHeader label="Valuation" sortKey="price" sort={inventorySort} onSort={() => setInventorySort(s => nextSort(s, 'price'))} />
                      <SortHeader label="Status" sortKey="status" sort={inventorySort} onSort={() => setInventorySort(s => nextSort(s, 'status'))} />
                      <PlainHeader label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryPageData.items.map((s, i) => (
                      <tr key={s.id} style={{
                        borderBottom: i < inventoryPageData.items.length - 1 ? '1px solid #F0F2F5' : 'none',
                        background: i % 2 === 0 ? '#fff' : '#FAFBFC',
                      }}>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ width: 44, height: 44, background: s.bg, overflow: 'hidden' }}>
                            <StoneVisual stone={s} size={44} />
                          </div>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <p style={{ fontFamily: 'DM Mono', fontSize: 11, fontWeight: 500, color: C.gold, marginBottom: 3 }}>{s.id}</p>
                          <p style={{ fontSize: 13, color: C.ink }}>{s.name}</p>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 9, background: '#f5f0ff', color: '#7048e8', padding: '3px 8px', fontWeight: 600, letterSpacing: '0.08em' }}>{s.author || 'UNKNOWN'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ fontFamily: 'DM Mono', fontSize: 13, fontWeight: 500, color: C.ink }}>${s.price.toLocaleString()}</span>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <StatusBadge status={s.status} />
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: 16 }}>
                            <button onClick={() => setEditingStone(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#3b5bdb', fontWeight: 500, letterSpacing: '0.06em' }}>
                              Edit
                            </button>
                            <button onClick={() => setDeleteTarget(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#e03131', fontWeight: 500, letterSpacing: '0.06em' }}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PaginationControls
                  page={inventoryPageData.currentPage}
                  totalPages={inventoryPageData.totalPages}
                  onPageChange={setInventoryPage}
                />
              </div>
            </>
          )}

          {/* ── Transactions ── */}
          {nav === 'transactions' && (
            <>
              {/* Stats row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 28 }}>
                {[
                  { label: 'Paid Transactions', val: payments.filter(p => p.status === 'PAID').length },
                  { label: 'Pending',           val: payments.filter(p => p.status === 'PENDING').length },
                  { label: 'Failed / Cancelled', val: payments.filter(p => ['FAILED', 'CANCELLED'].includes(p.status)).length },
                ].map(stat => (
                  <div key={stat.label} style={{ background: '#fff', padding: '20px 24px', border: '1px solid #E8EAED' }}>
                    <p style={{ fontSize: 10, color: C.muted, letterSpacing: '0.14em', fontWeight: 600, marginBottom: 8 }}>
                      {stat.label.toUpperCase()}
                    </p>
                    <p style={{ fontFamily: 'DM Mono', fontSize: 22, fontWeight: 500, color: C.ink }}>{stat.val}</p>
                  </div>
                ))}
              </div>

              {/* Transactions table */}
              <div style={{ background: '#fff', border: '1px solid #E8EAED', overflow: 'hidden' }}>
                {payments.length === 0 ? (
                  <div style={{ padding: '64px 0', textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Playfair Display', fontSize: 22, color: C.muted, fontWeight: 400 }}>No transactions yet</p>
                  </div>
                ) : (
                  <>
                    <TableToolbar
                      label="Transactions"
                      total={sortedPayments.length}
                      start={paymentPageData.start}
                      end={paymentPageData.end}
                      pageSize={paymentPageSize}
                      onPageSizeChange={setPaymentPageSize}
                    />
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #E8EAED' }}>
                          <SortHeader label="Transaction" sortKey="createdAt" sort={paymentSort} onSort={() => setPaymentSort(s => nextSort(s, 'createdAt'))} />
                          <SortHeader label="Treasure" sortKey="treasure" sort={paymentSort} onSort={() => setPaymentSort(s => nextSort(s, 'treasure'))} />
                          <SortHeader label="Customer" sortKey="customer" sort={paymentSort} onSort={() => setPaymentSort(s => nextSort(s, 'customer'))} />
                          <SortHeader label="Amount" sortKey="amount" sort={paymentSort} onSort={() => setPaymentSort(s => nextSort(s, 'amount'))} />
                          <SortHeader label="Provider" sortKey="provider" sort={paymentSort} onSort={() => setPaymentSort(s => nextSort(s, 'provider'))} />
                          <SortHeader label="Status" sortKey="status" sort={paymentSort} onSort={() => setPaymentSort(s => nextSort(s, 'status'))} />
                          <PlainHeader label="Details" />
                        </tr>
                      </thead>
                      <tbody>
                        {paymentPageData.items.map((p, i) => {
                          const order = p.order || {};
                          const expanded = expandedPayment === p._id;
                          return (
                            <Fragment key={p._id}>
                              <tr key={p._id} style={{
                                borderBottom: expanded ? 'none' : i < paymentPageData.items.length - 1 ? '1px solid #F0F2F5' : 'none',
                                background: i % 2 === 0 ? '#fff' : '#FAFBFC',
                              }}>
                              <td style={{ padding: '16px 16px' }}>
                                <p style={{ fontFamily: 'DM Mono', fontSize: 11, color: C.gold, marginBottom: 3 }}>{String(p._id).slice(-8)}</p>
                                <p style={{ fontSize: 10, color: C.muted }}>{new Date(p.createdAt).toLocaleString('en-GB')}</p>
                              </td>
                              <td style={{ padding: '16px 16px' }}>
                                <p style={{ fontFamily: 'DM Mono', fontSize: 11, color: C.gold, marginBottom: 3 }}>{p.treasureId || order.stoneId}</p>
                                <p style={{ fontSize: 13, color: C.ink }}>{order.stoneName || 'Treasure record'}</p>
                              </td>
                              <td style={{ padding: '16px 16px' }}>
                                <p style={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>{order.buyerName || '—'}</p>
                                <p style={{ fontFamily: 'DM Mono', fontSize: 11, color: C.muted, marginTop: 3 }}>{p.email}</p>
                              </td>
                              <td style={{ padding: '16px 16px' }}>
                                <span style={{ fontFamily: 'DM Mono', fontSize: 13, fontWeight: 500, color: C.ink }}>
                                  {p.currency} ${Number(p.amount || 0).toLocaleString()}
                                </span>
                              </td>
                              <td style={{ padding: '16px 16px' }}>
                                <p style={{ fontSize: 11, color: C.ink, fontWeight: 600, letterSpacing: '0.08em' }}>{String(p.provider || '').toUpperCase()}</p>
                                <p style={{ fontFamily: 'DM Mono', fontSize: 10, color: C.muted, marginTop: 3 }}>{p.providerPaymentId || '—'}</p>
                              </td>
                              <td style={{ padding: '16px 16px' }}>
                                <StatusBadge status={p.status} />
                              </td>
                              <td style={{ padding: '16px 16px' }}>
                                <button
                                  onClick={() => setExpandedPayment(expanded ? null : p._id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#3b5bdb', fontWeight: 600, letterSpacing: '0.06em', padding: 0 }}
                                >
                                  {expanded ? 'Hide' : 'View'}
                                </button>
                              </td>
                            </tr>
                            {expanded && (
                              <tr key={`${p._id}-detail`} style={{ borderBottom: i < filteredPayments.length - 1 ? '1px solid #F0F2F5' : 'none' }}>
                                <td colSpan={7} style={{ padding: '0 16px 18px', background: '#fff' }}>
                                  <div style={{ border: '1px solid #E8EAED', background: '#FAFBFC', padding: 18 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 16 }}>
                                      {[
                                        ['Payment ID', p._id],
                                        ['Order ID', p.orderId],
                                        ['Buyer Name', order.buyerName || '—'],
                                        ['Contact Number', order.contactNumber || '—'],
                                        ['Fulfillment', order.fulfillmentType || '—'],
                                        ['Delivery Address', order.deliveryAddress || '—'],
                                        ['Delivery Fee', `${p.currency || 'USD'} $${Number(order.deliveryFee || 0).toLocaleString()}`],
                                        ['Checkout URL', p.checkoutUrl || '—'],
                                        ['Created', new Date(p.createdAt).toLocaleString('en-GB')],
                                        ['Updated', new Date(p.updatedAt).toLocaleString('en-GB')],
                                        ['Order Status', order.status || '—'],
                                      ].map(([label, value]) => (
                                        <div key={label}>
                                          <p style={{ fontSize: 9, color: C.muted, letterSpacing: '0.14em', fontWeight: 700, marginBottom: 4 }}>{label.toUpperCase()}</p>
                                          <p style={{ fontFamily: 'DM Mono', fontSize: 11, color: C.ink, wordBreak: 'break-all' }}>{String(value || '—')}</p>
                                        </div>
                                      ))}
                                    </div>
                                    <pre style={{ margin: 0, maxHeight: 260, overflow: 'auto', background: '#0F172A', color: '#dbeafe', padding: 14, fontSize: 11, lineHeight: 1.5 }}>
                                      {JSON.stringify({ rawRequest: p.rawRequest, rawResponse: p.rawResponse, rawCallback: p.rawCallback }, null, 2)}
                                    </pre>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    <PaginationControls
                      page={paymentPageData.currentPage}
                      totalPages={paymentPageData.totalPages}
                      onPageChange={setPaymentPage}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {/* ── Settings ── */}
          {nav === 'settings' && (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <p style={{ fontFamily: 'Playfair Display', fontSize: 28, color: C.muted, fontWeight: 400 }}>Settings</p>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 12 }}>Collection configuration & access management</p>
            </div>
          )}
        </div>
      </main>

      {deleteTarget && (
        <div
          role="presentation"
          onClick={() => { if (!deletingId) setDeleteTarget(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(15, 23, 42, 0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
            onClick={event => event.stopPropagation()}
            style={{ width: 'min(100%, 460px)', background: '#fff', border: '1px solid #E8EAED', boxShadow: '0 22px 70px rgba(15, 23, 42, 0.24)', padding: '30px 32px 28px' }}
          >
            <p style={{ fontSize: 10, color: '#e03131', letterSpacing: '0.18em', fontWeight: 700, marginBottom: 10 }}>DELETE TREASURE</p>
            <h2 id="delete-dialog-title" style={{ fontFamily: 'Playfair Display', fontSize: 28, lineHeight: 1.2, fontWeight: 400, color: C.ink, marginBottom: 12 }}>Remove this record?</h2>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
              “{deleteTarget.name}” ({deleteTarget.id}) will be permanently removed from the inventory.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" disabled={Boolean(deletingId)} onClick={() => setDeleteTarget(null)} style={{ border: '1px solid #DDE1E6', background: '#fff', color: C.muted, padding: '11px 18px', cursor: deletingId ? 'not-allowed' : 'pointer', fontSize: 11, letterSpacing: '0.08em', fontWeight: 700 }}>
                CANCEL
              </button>
              <button type="button" disabled={Boolean(deletingId)} onClick={() => deleteRow(deleteTarget.id)} style={{ border: '1px solid #e03131', background: '#e03131', color: '#fff', padding: '11px 18px', cursor: deletingId ? 'wait' : 'pointer', fontSize: 11, letterSpacing: '0.08em', fontWeight: 700, opacity: deletingId ? 0.7 : 1 }}>
                {deletingId ? 'DELETING…' : 'DELETE RECORD'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function PlainHeader({ label }) {
  return (
    <th style={{
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: 9,
      color: C.muted,
      letterSpacing: '0.18em',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {label.toUpperCase()}
    </th>
  );
}

function SortHeader({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th style={{ padding: 0, textAlign: 'left', whiteSpace: 'nowrap' }}>
      <button onClick={onSort} style={{
        width: '100%',
        padding: '12px 16px',
        border: 'none',
        background: active ? '#FAFBFC' : 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: 9,
        color: active ? C.ink : C.muted,
        letterSpacing: '0.18em',
        fontWeight: 700,
      }}>
        {label.toUpperCase()}
        <span style={{ marginLeft: 6, color: active ? C.gold : '#CBD5E1', fontFamily: 'DM Mono' }}>
          {active ? (sort.direction === 'asc' ? 'UP' : 'DOWN') : '--'}
        </span>
      </button>
    </th>
  );
}

function TableToolbar({ label, total, start, end, pageSize, onPageSizeChange }) {
  const from = total === 0 ? 0 : start + 1;
  return (
    <div style={{
      minHeight: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      padding: '0 16px',
      borderBottom: '1px solid #E8EAED',
      background: '#fff',
    }}>
      <p style={{ fontSize: 11, color: C.muted, letterSpacing: '0.08em', fontWeight: 600 }}>
        {label.toUpperCase()} · SHOWING {from.toLocaleString()}-{end.toLocaleString()} OF {total.toLocaleString()}
      </p>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: C.muted, letterSpacing: '0.12em', fontWeight: 700 }}>
        ROWS
        <select value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))} style={{
          border: '1px solid #E8EAED',
          background: '#fff',
          color: C.ink,
          padding: '7px 26px 7px 9px',
          fontFamily: 'DM Mono',
          fontSize: 11,
          outline: 'none',
        }}>
          {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size}</option>)}
        </select>
      </label>
    </div>
  );
}

function PaginationControls({ page, totalPages, onPageChange }) {
  const buttonStyle = (disabled) => ({
    minWidth: 72,
    padding: '8px 12px',
    border: '1px solid #E8EAED',
    background: disabled ? '#F8F9FA' : '#fff',
    color: disabled ? '#CBD5E1' : C.ink,
    cursor: disabled ? 'default' : 'pointer',
    fontSize: 10,
    letterSpacing: '0.12em',
    fontWeight: 700,
  });

  return (
    <div style={{
      minHeight: 54,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 8,
      padding: '0 16px',
      borderTop: '1px solid #E8EAED',
      background: '#fff',
    }}>
      <button disabled={page <= 1} onClick={() => onPageChange(1)} style={buttonStyle(page <= 1)}>FIRST</button>
      <button disabled={page <= 1} onClick={() => onPageChange(page - 1)} style={buttonStyle(page <= 1)}>PREV</button>
      <span style={{ minWidth: 96, textAlign: 'center', fontFamily: 'DM Mono', fontSize: 11, color: C.muted }}>
        {page} / {totalPages}
      </span>
      <button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} style={buttonStyle(page >= totalPages)}>NEXT</button>
      <button disabled={page >= totalPages} onClick={() => onPageChange(totalPages)} style={buttonStyle(page >= totalPages)}>LAST</button>
    </div>
  );
}
